import {
  ToolkitPropertyEnum,
  isDefined,
  isToolkitListingID,
  isUndefined,
} from '@aloft/toolkit-shared';

import { Map } from 'mapbox-gl';
import { MutableRefObject, useEffect } from 'react';

import { getPropertyPinIcon } from '../../../components/Tkv2Map/utils/getPropertyPinIcon.ts';
import MapBox from '../../../components/map/MapBox.ts';
import { MAP_LAYERS, MAP_SOURCES } from '../../../components/map/constants.ts';
import { useTkv2ReferenceBarUserInterface } from '../../../controller/hooks/useTkv2ReferenceBarUserInterface.ts';
import { selectReferenceBarUI } from '../../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { MapCoords } from '../hooks/useMapCoords.ts';
import { fitBounds } from './fitBounds.ts';
import { useCompSequence } from './useCompSequence.ts';

type CompLatLong = {
  latitude: number;
  longitude: number;
  status: string | undefined;
  idx: number;
  id: string;
};

const useCompLatLongs = (mapData: MapCoords): CompLatLong[] => {
  const compSequence = useCompSequence();
  const subjectPinCoordinates = mapData.results.subject;

  const { inPairedSales } = useTkv2ReferenceBarUserInterface();
  const { properties } = useAppSelector(selectReferenceBarUI);
  const pairedSalesProperties = properties.filter((property) => isToolkitListingID(property.id));

  if (mapData.isLoading) {
    return [];
  }

  const compLatLongs: CompLatLong[] = (
    inPairedSales ? pairedSalesProperties.map((p) => p.id) : compSequence
  )
    .map((comp, idx) => {
      const compCoordinates = mapData.results[comp];
      if (isUndefined(compCoordinates)) {
        return undefined;
      }

      return {
        latitude: compCoordinates?.lat,
        longitude: compCoordinates?.lng,
        status: compCoordinates?.mlsStatus,
        idx: idx + 1,
        id: comp,
      };
    })
    .filter(isDefined);

  if (subjectPinCoordinates) {
    compLatLongs.push({
      latitude: subjectPinCoordinates.lat,
      longitude: subjectPinCoordinates.lng,
      status: 'Subject',
      idx: 0,
      id: 'subject',
    });
  }

  return compLatLongs;
};

const loadPins = (map: Map | undefined, compLatLongs: CompLatLong[], activeIndex?: number) => {
  if (isUndefined(map)) {
    return;
  }

  const features = compLatLongs
    .map(({ latitude, longitude, status, idx, id }) => {
      const isFocused = idx === activeIndex;
      const icon = getPropertyPinIcon(
        status as ToolkitPropertyEnum<'ListingStatusType'>,
        isFocused,
        idx,
      );

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [longitude, latitude],
        },
        properties: {
          icon,
          size: isFocused ? 1.5 : 1,
          id,
          idx,
        },
      };
    })
    .filter(isDefined);

  const currentSource = map?.getSource(MAP_SOURCES.PINS);
  if (!currentSource) {
    map?.addSource(MAP_SOURCES.PINS, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    // Add the pins layer if it does not exist
    if (!map.getLayer(MAP_LAYERS.PINS)) {
      map.addLayer({
        id: MAP_LAYERS.PINS,
        type: 'symbol',
        source: MAP_SOURCES.PINS,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': ['get', 'size'],
        },
      });
    }
  }
  const source = currentSource ?? map?.getSource(MAP_SOURCES.PINS);

  if (source?.type !== 'geojson' || !('setData' in source)) return;

  source.setData({
    type: 'FeatureCollection',
    features,
  });

  if (compLatLongs.length > 0) {
    fitBounds(map, compLatLongs);
  }
};

export const useAddMapPins = (mapData: MapCoords, mapRef: MutableRefObject<MapBox | null>) => {
  const { inPairedSales } = useTkv2ReferenceBarUserInterface();
  const { properties } = useAppSelector(selectReferenceBarUI);
  const compCoords = useCompLatLongs(mapData);

  // we only highlight the active property in paired cost, when we are mapping
  // comps.
  const firstProperty = properties && properties[0];
  const activeIndex = !inPairedSales ? +(firstProperty?.mapPinLabel || '') : undefined;

  useEffect(() => {
    const map = mapRef.current?.getMap();

    if (!map?.loaded()) {
      // if the map hasn't loaded yet, wait for it to load before adding pins
      map?.on('load', () => {
        loadPins(map, compCoords, activeIndex);
      });
      return;
    }

    // update pins to match current state
    loadPins(map, compCoords, activeIndex);
  }, [compCoords, mapRef, activeIndex]);
};
